// src/components/layout/views/AutoArchitectView.jsx
// FINAL PRODUCTION VERSION - Expert Approved
// Complete wizard with real-time requirement saving, traceability, and enhanced UX

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiZap, FiCheckCircle, FiAlertTriangle, FiRefreshCw,
  FiChevronDown, FiChevronUp, FiBox, FiDollarSign,
  FiSearch, FiX, FiDownload, FiTrendingUp, FiShield,
  FiLayers, FiTarget, FiCheck, FiPlus, FiInfo,
  FiFilter, FiStar, FiThumbsUp, FiAward, FiArrowRight,
  FiArrowLeft, FiSave, FiClipboard, FiCpu, FiDatabase,
  FiServer, FiMonitor, FiGitBranch, FiActivity, FiUsers,
  FiClock, FiGlobe, FiLock, FiSettings, FiChevronRight,
  FiMaximize, FiMinimize, FiEye, FiEyeOff, FiBarChart2,
  FiPackage, FiTag, FiFolder, FiBriefcase, FiHome, FiGrid
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { generateUniversalArchitecture } from '../../../utils/autoArchitect';
import { UNIVERSAL_INDUSTRIES, UNIVERSAL_DATA_TYPES, UNIVERSAL_PROBLEM_TYPES } from '../../../data/universalIndustries';
import { generateTraceabilityData } from '../../../utils/traceabilityEngine';

// ==================== PROBLEM TYPE MAPPING ====================

const PROBLEM_TYPE_MAPPING = {
  'classification': ['classification', 'segmentation'],
  'diagnosis': ['classification', 'diagnosis'],
  'fraud': ['classification', 'anomaly', 'fraud'],
  'churn': ['classification', 'regression', 'risk'],
  'quality': ['classification', 'quality', 'detection'],
  'prediction': ['regression', 'forecasting'],
  'forecast': ['regression', 'forecasting', 'time-series'],
  'demand': ['regression', 'forecasting'],
  'yield': ['regression', 'optimization'],
  'price': ['regression', 'optimization'],
  'anomaly': ['anomaly', 'detection'],
  'defect': ['anomaly', 'detection', 'quality'],
  'leak': ['anomaly', 'detection'],
  'threat': ['anomaly', 'detection', 'risk'],
  'recommend': ['recommendation', 'personalization'],
  'personalize': ['personalization', 'recommendation'],
  'match': ['matching', 'recommendation'],
  'segment': ['segmentation', 'clustering'],
  'nlp': ['nlp', 'text-processing'],
  'sentiment': ['sentiment', 'nlp'],
  'text': ['nlp', 'extraction', 'summarization'],
  'document': ['nlp', 'extraction', 'classification'],
  'chatbot': ['chatbot', 'nlp'],
  'translation': ['translation', 'nlp'],
  'summarization': ['summarization', 'nlp'],
  'vision': ['vision', 'detection'],
  'image': ['vision', 'classification', 'detection'],
  'visual': ['vision', 'detection'],
  'inspection': ['vision', 'quality', 'detection'],
  'monitor': ['monitoring', 'detection'],
  'optimization': ['optimization', 'planning'],
  'scheduling': ['planning', 'optimization', 'scheduling'],
  'routing': ['routing', 'optimization'],
  'capacity': ['optimization', 'forecasting'],
  'automation': ['automation', 'optimization'],
  'process': ['automation', 'optimization'],
  'maintenance': ['maintenance', 'prediction', 'anomaly'],
  'predictive': ['maintenance', 'prediction', 'forecasting'],
  'risk': ['risk', 'classification'],
  'compliance': ['risk', 'classification', 'monitoring'],
  'analysis': ['classification', 'regression', 'segmentation'],
  'detection': ['detection', 'anomaly'],
  'recognition': ['detection', 'vision', 'classification'],
  'tracking': ['detection', 'monitoring'],
  'planning': ['planning', 'optimization'],
  'forecasting': ['forecasting', 'regression'],
  'scoring': ['ranking', 'classification'],
  'ranking': ['ranking', 'classification'],
  'search': ['search', 'ranking'],
  'extraction': ['extraction', 'nlp'],
  'generation': ['generation'],
  'reinforcement': ['reinforcement'],
  'monitoring': ['monitoring', 'detection']
};

// ==================== STEPS ====================

const STEPS = [
  { id: 'industry', label: 'Industry', icon: FiBriefcase, description: 'Select your industry' },
  { id: 'problems', label: 'Problems', icon: FiTarget, description: 'Select business problems' },
  { id: 'problemTypes', label: 'Problem Types', icon: FiGrid, description: 'Auto-configured' },
  { id: 'dataTypes', label: 'Data Types', icon: FiDatabase, description: 'Auto-configured' },
  { id: 'solution', label: 'Solution', icon: FiSettings, description: 'Solution details' },
  { id: 'technical', label: 'Technical', icon: FiCpu, description: 'Technical requirements' },
  { id: 'review', label: 'Review', icon: FiEye, description: 'Review and generate' }
];

// ==================== MAIN COMPONENT ====================

const AutoArchitectView = () => {
  const { state, dispatch, saveAutoArchitectRequirements } = useSolution();
  
  // ==================== REQUIREMENTS STATE ====================
  const [requirements, setRequirements] = useState(() => ({
    industry: state.autoArchitectRequirements?.industry || '',
    subIndustry: state.autoArchitectRequirements?.subIndustry || '',
    problems: state.autoArchitectRequirements?.problems || [],
    problemTypes: state.autoArchitectRequirements?.problemTypes || [],
    dataTypes: state.autoArchitectRequirements?.dataTypes || [],
    scale: state.autoArchitectRequirements?.scale || 'smb',
    latency: state.autoArchitectRequirements?.latency || 'standard',
    budget: state.autoArchitectRequirements?.budget || 'medium',
    expertise: state.autoArchitectRequirements?.expertise || 'intermediate',
    deployment: state.autoArchitectRequirements?.deployment || 'cloud',
    compliance: state.autoArchitectRequirements?.compliance || 'medium',
    timeframe: state.autoArchitectRequirements?.timeframe || '3-months',
    priority: state.autoArchitectRequirements?.priority || 'balanced',
    dataVolume: state.autoArchitectRequirements?.dataVolume || 'medium',
    securityLevel: state.autoArchitectRequirements?.securityLevel || 'standard',
    teamSize: state.autoArchitectRequirements?.teamSize || '1-5',
    integrationNeeds: state.autoArchitectRequirements?.integrationNeeds || []
  }));
  
  // ==================== ENHANCED STATE ====================
  const [solutionMeta, setSolutionMeta] = useState({
    version: '1.0.0',
    author: '',
    stakeholders: [],
    solutionType: 'greenfield',
    deploymentType: 'standalone',
    environmentType: 'poc',
    reusability: 'single-use',
    audience: 'internal'
  });

  const [industryDetails, setIndustryDetails] = useState({
    subIndustry: '',
    region: 'global',
    maturity: 'emerging'
  });

  const [scalingDetails, setScalingDetails] = useState({
    expectedUsers: '',
    growthRate: '20',
    peakLoadMultiplier: '2',
    slaLevel: '99.9',
    p99Latency: '',
    capexVsOpex: 'opex',
    roiExpectation: '200',
    paybackPeriod: '12',
    multiCloud: false,
    cloudProviders: [],
    dataResidency: 'no-specific',
    auditLevel: 'basic',
    networkSecurity: 'standard',
    teamSize: '1-5',
    technicalSkills: [],
    trainingNeeds: [],
    timeToMarket: '3-months',
    innovationLevel: 'follower'
  });

  const [sizingInputs, setSizingInputs] = useState({
    expectedUsers: '',
    expectedDataVolume: '',
    expectedTPS: '',
    growthRate: '20',
    peakLoadMultiplier: '2'
  });

  // ==================== UI STATE ====================
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSolution, setGeneratedSolution] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const [showAutoConfigInfo, setShowAutoConfigInfo] = useState(false);
  const [searchIndustry, setSearchIndustry] = useState('');
  const [searchProblem, setSearchProblem] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const resultsRef = useRef(null);
  const mainContainerRef = useRef(null);

  const industryList = Object.entries(UNIVERSAL_INDUSTRIES);
  const selectedIndustryData = requirements.industry ? UNIVERSAL_INDUSTRIES[requirements.industry] : null;

  // ==================== SAVE REQUIREMENTS TO CONTEXT ====================
  
  const saveRequirementsToContext = useCallback((updatedRequirements) => {
    console.log('💾 Saving Requirements:', {
      industry: updatedRequirements.industry,
      subIndustry: updatedRequirements.subIndustry,
      problems: updatedRequirements.problems?.length,
      problemTypes: updatedRequirements.problemTypes?.length,
      dataTypes: updatedRequirements.dataTypes?.length
    });
    
    dispatch({ 
      type: 'SAVE_AUTO_ARCHITECT_REQUIREMENTS', 
      payload: updatedRequirements 
    });
  }, [dispatch]);

  // ==================== AUTO-CONFIGURE ====================
  
  const autoConfigureFromProblems = useCallback((selectedProblems, industryData) => {
    if (!selectedProblems || selectedProblems.length === 0 || !industryData) {
      return { problemTypes: [], dataTypes: [] };
    }
    
    const mappedProblemTypes = new Set();
    const mappedDataTypes = new Set();
    
    selectedProblems.forEach(problemId => {
      const problem = industryData.problems?.find(p => p.id === problemId);
      if (!problem) return;
      
      const problemNameLower = problem.name.toLowerCase();
      const problemIdLower = problem.id.toLowerCase();
      
      Object.entries(PROBLEM_TYPE_MAPPING).forEach(([keyword, types]) => {
        if (problemNameLower.includes(keyword) || problemIdLower.includes(keyword)) {
          types.forEach(t => {
            if (UNIVERSAL_PROBLEM_TYPES.some(pt => pt.id === t)) {
              mappedProblemTypes.add(t);
            }
          });
        }
      });
      
      const autoMapRules = [
        { keywords: ['predict', 'forecast'], types: ['forecasting', 'regression'] },
        { keywords: ['detect', 'identify'], types: ['detection'] },
        { keywords: ['classif', 'categor'], types: ['classification'] },
        { keywords: ['recommend', 'suggest'], types: ['recommendation'] },
        { keywords: ['optimiz', 'improve'], types: ['optimization'] },
        { keywords: ['analyz', 'insight'], types: ['segmentation', 'classification'] },
        { keywords: ['chat', 'conversation', 'assistant'], types: ['chatbot', 'nlp'] },
        { keywords: ['image', 'visual', 'video'], types: ['vision', 'detection'] },
        { keywords: ['text', 'document', 'language'], types: ['nlp'] },
        { keywords: ['sentiment', 'opinion', 'emotion'], types: ['sentiment', 'nlp'] },
        { keywords: ['fraud', 'anomaly', 'unusual'], types: ['anomaly', 'fraud'] },
        { keywords: ['risk', 'threat'], types: ['risk', 'classification'] },
        { keywords: ['quality', 'inspection'], types: ['quality', 'detection'] },
        { keywords: ['maintenance', 'failure', 'wear'], types: ['maintenance', 'anomaly'] },
        { keywords: ['routing', 'route', 'path'], types: ['routing', 'optimization'] },
        { keywords: ['scheduling', 'planning', 'allocation'], types: ['planning', 'scheduling'] },
        { keywords: ['pricing', 'price'], types: ['regression', 'optimization'] },
        { keywords: ['automation', 'automated'], types: ['automation'] },
        { keywords: ['monitor', 'track'], types: ['monitoring', 'detection'] },
        { keywords: ['segment', 'cluster', 'group'], types: ['segmentation'] },
        { keywords: ['generat', 'creat'], types: ['generation'] },
        { keywords: ['search', 'retriev'], types: ['search', 'ranking'] },
        { keywords: ['extract', 'parse'], types: ['extraction', 'nlp'] },
        { keywords: ['summar', 'condens'], types: ['summarization', 'nlp'] },
        { keywords: ['translat'], types: ['translation'] },
        { keywords: ['churn', 'attrition', 'retention'], types: ['classification', 'regression'] },
        { keywords: ['demand', 'forecast'], types: ['forecasting', 'regression'] },
        { keywords: ['inventory', 'stock'], types: ['optimization', 'forecasting'] },
        { keywords: ['energy', 'consumption'], types: ['optimization', 'forecasting'] }
      ];
      
      autoMapRules.forEach(rule => {
        if (rule.keywords.some(kw => problemNameLower.includes(kw))) {
          rule.types.forEach(t => mappedProblemTypes.add(t));
        }
      });
      
      if (mappedProblemTypes.size === 0) {
        mappedProblemTypes.add('classification');
        mappedProblemTypes.add('regression');
      }
      
      problem.dataTypes?.forEach(dt => mappedDataTypes.add(dt));
    });
    
    return {
      problemTypes: Array.from(mappedProblemTypes),
      dataTypes: Array.from(mappedDataTypes)
    };
  }, []);

  // ==================== HANDLERS ====================
  
  const handleIndustrySelect = useCallback((industryId) => {
    const updatedRequirements = {
      ...requirements,
      industry: industryId,
      subIndustry: '',
      problems: [],
      problemTypes: [],
      dataTypes: []
    };
    setRequirements(updatedRequirements);
    saveRequirementsToContext(updatedRequirements);
    setError(null);
    setShowAutoConfigInfo(false);
  }, [requirements, saveRequirementsToContext]);

  const handleSubIndustrySelect = useCallback((subIndustry) => {
    const updatedRequirements = { ...requirements, subIndustry };
    setRequirements(updatedRequirements);
    saveRequirementsToContext(updatedRequirements);
  }, [requirements, saveRequirementsToContext]);

  const toggleProblem = useCallback((problemId) => {
    setRequirements(prev => {
      const currentProblems = [...prev.problems];
      const index = currentProblems.indexOf(problemId);
      const newProblems = index >= 0 
        ? currentProblems.filter(p => p !== problemId) 
        : [...currentProblems, problemId];
      
      const industryData = UNIVERSAL_INDUSTRIES[prev.industry];
      const autoConfig = autoConfigureFromProblems(newProblems, industryData);
      
      const updatedRequirements = {
        ...prev,
        problems: newProblems,
        problemTypes: autoConfig.problemTypes,
        dataTypes: autoConfig.dataTypes
      };
      
      setTimeout(() => saveRequirementsToContext(updatedRequirements), 0);
      return updatedRequirements;
    });
    
    setShowAutoConfigInfo(true);
    setTimeout(() => setShowAutoConfigInfo(false), 5000);
  }, [autoConfigureFromProblems, saveRequirementsToContext]);

  const selectAllProblems = useCallback(() => {
    const industryData = UNIVERSAL_INDUSTRIES[requirements.industry];
    if (!industryData) return;
    const allProblemIds = industryData.problems?.map(p => p.id) || [];
    const autoConfig = autoConfigureFromProblems(allProblemIds, industryData);
    
    const updatedRequirements = {
      ...requirements,
      problems: allProblemIds,
      problemTypes: autoConfig.problemTypes,
      dataTypes: autoConfig.dataTypes
    };
    setRequirements(updatedRequirements);
    saveRequirementsToContext(updatedRequirements);
  }, [requirements, autoConfigureFromProblems, saveRequirementsToContext]);

  const clearAllProblems = useCallback(() => {
    const updatedRequirements = {
      ...requirements,
      problems: [],
      problemTypes: [],
      dataTypes: []
    };
    setRequirements(updatedRequirements);
    saveRequirementsToContext(updatedRequirements);
  }, [requirements, saveRequirementsToContext]);

  const toggleProblemType = useCallback((typeId) => {
    setRequirements(prev => {
      const current = [...prev.problemTypes];
      const index = current.indexOf(typeId);
      if (index >= 0) current.splice(index, 1);
      else current.push(typeId);
      const updated = { ...prev, problemTypes: current };
      saveRequirementsToContext(updated);
      return updated;
    });
  }, [saveRequirementsToContext]);

  const toggleDataType = useCallback((typeId) => {
    setRequirements(prev => {
      const current = [...prev.dataTypes];
      const index = current.indexOf(typeId);
      if (index >= 0) current.splice(index, 1);
      else current.push(typeId);
      const updated = { ...prev, dataTypes: current };
      saveRequirementsToContext(updated);
      return updated;
    });
  }, [saveRequirementsToContext]);

  const handleTechnicalChange = useCallback((field, value) => {
    setRequirements(prev => {
      const updated = { ...prev, [field]: value };
      saveRequirementsToContext(updated);
      return updated;
    });
  }, [saveRequirementsToContext]);

  const handleGenerate = useCallback(() => {
    if (!requirements.industry) {
      setError('Please select an industry');
      return;
    }
    if (requirements.problems.length === 0) {
      setError('Please select at least one problem');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    const enhancedRequirements = {
      ...requirements,
      solutionMeta,
      industryDetails,
      scalingDetails,
      sizingInputs: {
        expectedUsers: scalingDetails.expectedUsers || sizingInputs.expectedUsers,
        expectedDataVolume: sizingInputs.expectedDataVolume,
        expectedTPS: sizingInputs.expectedTPS,
        growthRate: scalingDetails.growthRate || sizingInputs.growthRate,
        peakLoadMultiplier: scalingDetails.peakLoadMultiplier || sizingInputs.peakLoadMultiplier
      }
    };
    
    saveRequirementsToContext(enhancedRequirements);
    
    setTimeout(() => {
      try {
        const result = generateUniversalArchitecture(enhancedRequirements);
        
        // Generate traceability data
        const traceabilityData = generateTraceabilityData(
          enhancedRequirements,
          result.selectedComponents,
          result.selectedApproaches || [],
          requirements.problemTypes,
          requirements.dataTypes
        );
        
        // Attach traceability to result
        result.traceability = traceabilityData;
        result.functionalBOM = traceabilityData.functionalBOM;
        result.technicalBOM = traceabilityData.technicalBOM;
        result.justificationMap = traceabilityData.justificationMap;
        result.confidenceScores = traceabilityData.confidenceScores;
        result.selectionRationale = traceabilityData.selectionRationale;
        
        setGeneratedSolution(result);
        setIsGenerating(false);
        setShowResults(true);
        
        dispatch({ type: 'SAVE_ADDITIONAL_INPUTS', payload: enhancedRequirements });
        dispatch({ type: 'SAVE_TRACEABILITY_DATA', payload: traceabilityData });
        dispatch({ type: 'CLEAR_SELECTION' });
        
        result.selectedComponents.forEach(comp => {
          const enrichedComp = {
            ...comp,
            justification: traceabilityData.justificationMap[comp.id] || '',
            confidence: traceabilityData.confidenceScores[comp.id] || 70,
            selectionRationale: traceabilityData.selectionRationale[comp.id] || [],
            useCases: traceabilityData.components.find(c => c.id === comp.id)?.useCases || [],
            requirements: traceabilityData.components.find(c => c.id === comp.id)?.requirements || []
          };
          dispatch({ type: 'ADD_COMPONENT', payload: enrichedComp });
        });
        
        dispatch({
          type: 'CREATE_SOLUTION',
          payload: {
            name: `${requirements.industry}-${Date.now()}`,
            description: `${selectedIndustryData?.name} - ${requirements.problems.join(', ')}`,
            requirements: enhancedRequirements,
            traceabilityData
          }
        });
        
        dispatch({
          type: 'SAVE_CONSTRAINTS',
          payload: {
            budget: requirements.budget,
            time: requirements.timeframe,
            quality: requirements.priority,
            scale: requirements.scale,
            latency: requirements.latency,
            deployment: requirements.deployment,
            compliance: requirements.compliance,
            security: requirements.securityLevel,
            expertise: requirements.expertise,
            problemTypes: requirements.problemTypes,
            dataTypes: requirements.dataTypes,
            industry: requirements.industry,
            subIndustry: requirements.subIndustry,
            problems: requirements.problems
          }
        });
        
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 500);
        
      } catch (err) {
        console.error('Generation error:', err);
        setError('Failed to generate architecture. Please check your selections.');
        setIsGenerating(false);
      }
    }, 2000);
  }, [requirements, solutionMeta, industryDetails, scalingDetails, sizingInputs, selectedIndustryData, dispatch, saveRequirementsToContext]);

  const toggleFullscreen = useCallback(() => {
    const el = mainContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);

  // ==================== FILTERED LISTS ====================
  
  const filteredIndustries = useMemo(() => {
    if (!searchIndustry) return industryList;
    return industryList.filter(([id, ind]) => 
      ind.name.toLowerCase().includes(searchIndustry.toLowerCase())
    );
  }, [industryList, searchIndustry]);

  const filteredProblems = useMemo(() => {
    if (!selectedIndustryData?.problems) return [];
    if (!searchProblem) return selectedIndustryData.problems;
    return selectedIndustryData.problems.filter(p => 
      p.name.toLowerCase().includes(searchProblem.toLowerCase())
    );
  }, [selectedIndustryData, searchProblem]);

  // ==================== PROGRESS ====================
  
  const getProgress = useMemo(() => {
    let progress = 0;
    if (requirements.industry) progress = 20;
    if (requirements.problems.length > 0) progress = 40;
    if (requirements.problemTypes.length > 0) progress = 60;
    if (requirements.dataTypes.length > 0) progress = 80;
    if (showResults) progress = 100;
    return progress;
  }, [requirements, showResults]);

  // ==================== RENDER ====================

  return (
    <div 
      ref={mainContainerRef}
      className={`h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 ${isFullscreen ? 'bg-white' : ''}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* HEADER */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <FiZap className="w-8 h-8 text-yellow-500" />
            Universal AI Solution Architect
          </h2>
          <p className="text-gray-600">
            {Object.keys(UNIVERSAL_INDUSTRIES).length} Industries • {UNIVERSAL_PROBLEM_TYPES.length} Problem Types • {UNIVERSAL_DATA_TYPES.length} Data Types
          </p>
        </div>

        {/* STEP NAVIGATION */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200 p-2">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const isActive = currentStep === i;
            const isComplete = getProgress >= (i+1)*(100/STEPS.length);
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(i)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  isActive ? 'bg-blue-500 text-white shadow-md' : 
                  isComplete ? 'bg-green-100 text-green-700' : 
                  'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {step.label}
                {isComplete && <FiCheck className="w-3 h-3" />}
              </button>
            );
          })}
          <div className="ml-auto">
            <button onClick={toggleFullscreen} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
              {isFullscreen ? <FiMinimize className="w-4 h-4" /> : <FiMaximize className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* PROGRESS BAR */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <motion.div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full"
              animate={{ width: `${getProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* ERROR */}
        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
            >
              <FiAlertTriangle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-800">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto"><FiX className="w-4 h-4 text-red-500" /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* STEP 1: INDUSTRY */}
        <AnimatePresence mode="wait">
          {currentStep === 0 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-blue-50 border-b border-blue-100 flex items-center gap-3">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <h3 className="text-lg font-bold text-gray-900">Select Industry</h3>
                {requirements.industry && (
                  <span className="ml-auto text-sm text-green-600 flex items-center gap-1">
                    <FiCheckCircle /> {UNIVERSAL_INDUSTRIES[requirements.industry]?.name}
                  </span>
                )}
              </div>
              
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input type="text" placeholder="Search industries..." value={searchIndustry} onChange={(e) => setSearchIndustry(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-80 overflow-y-auto">
                  {filteredIndustries.map(([id, ind]) => (
                    <button
                      key={id}
                      onClick={() => handleIndustrySelect(id)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        requirements.industry === id ? 'border-blue-500 bg-blue-50 shadow-md transform scale-105' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{ind.icon}</div>
                      <div className="text-xs font-semibold">{ind.name}</div>
                      <div className="text-[10px] text-gray-500 mt-1">{ind.problems?.length || 0} problems</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {requirements.industry && selectedIndustryData?.subIndustries && (
                <div className="p-4 border-t border-gray-100">
                  <label className="block text-sm font-semibold mb-2">Sub-Industry (Optional)</label>
                  <select 
                    value={requirements.subIndustry} 
                    onChange={(e) => handleSubIndustrySelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select Sub-Industry</option>
                    {selectedIndustryData.subIndustries.map(si => (
                      <option key={si} value={si}>{si}</option>
                    ))}
                  </select>
                </div>
              )}
            </motion.div>
          )}

          {/* STEP 2: PROBLEMS */}
          {currentStep === 1 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-green-50 border-b border-green-100 flex items-center gap-3 flex-wrap">
                <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <h3 className="text-lg font-bold text-gray-900">Select Problems (Multi-Select)</h3>
                <span className="ml-auto text-sm text-green-600 font-semibold">{requirements.problems.length} selected</span>
                <button onClick={selectAllProblems} className="text-xs text-blue-600 hover:underline">Select All</button>
                <button onClick={clearAllProblems} className="text-xs text-red-600 hover:underline">Clear All</button>
              </div>
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input type="text" placeholder="Search problems..." value={searchProblem} onChange={(e) => setSearchProblem(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                  {filteredProblems.map(problem => {
                    const isSelected = requirements.problems.includes(problem.id);
                    return (
                      <button
                        key={problem.id}
                        onClick={() => toggleProblem(problem.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-left relative ${
                          isSelected ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <FiCheck className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{problem.icon}</span>
                          <span className="font-semibold text-gray-900 text-sm">{problem.name}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {problem.dataTypes?.slice(0, 4).map(dt => (
                            <span key={dt} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">{dt}</span>
                          ))}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 3: PROBLEM TYPES */}
          {currentStep === 2 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-purple-50 border-b border-purple-100 flex items-center gap-3">
                <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <h3 className="text-lg font-bold text-gray-900">Problem Types <span className="text-xs text-purple-600 font-normal">(Auto-Configured)</span></h3>
                <span className="ml-auto text-sm text-purple-600 font-semibold">{requirements.problemTypes.length} selected</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-80 overflow-y-auto">
                  {UNIVERSAL_PROBLEM_TYPES.map(type => {
                    const isSelected = requirements.problemTypes.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => toggleProblemType(type.id)}
                        className={`p-3 rounded-xl border-2 transition-all text-left relative ${
                          isSelected ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <FiCheck className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{type.icon}</span>
                          <span className="font-semibold text-gray-900 text-sm">{type.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 4: DATA TYPES */}
          {currentStep === 3 && (
            <motion.div key="step4" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-orange-50 border-b border-orange-100 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <h3 className="text-lg font-bold text-gray-900">Data Types <span className="text-xs text-orange-600 font-normal">(Auto-Configured)</span></h3>
                <span className="ml-auto text-sm text-orange-600 font-semibold">{requirements.dataTypes.length} selected</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-80 overflow-y-auto">
                  {UNIVERSAL_DATA_TYPES.map(type => {
                    const isSelected = requirements.dataTypes.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => toggleDataType(type.id)}
                        className={`p-3 rounded-xl border-2 transition-all text-left relative ${
                          isSelected ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <FiCheck className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{type.icon}</span>
                          <span className="font-semibold text-gray-900 text-sm">{type.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 5: TECHNICAL DETAILS */}
          {currentStep === 4 && (
            <motion.div key="step5" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-teal-50 border-b border-teal-100 flex items-center gap-3">
                <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">5</span>
                <h3 className="text-lg font-bold text-gray-900">Technical Details</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">Scale</label>
                    <select value={requirements.scale} onChange={(e) => handleTechnicalChange('scale', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="startup">Startup</option>
                      <option value="smb">SMB</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Latency</label>
                    <select value={requirements.latency} onChange={(e) => handleTechnicalChange('latency', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="batch">Batch</option>
                      <option value="standard">Standard</option>
                      <option value="real-time">Real-time</option>
                      <option value="ultra-low">Ultra-low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Budget</label>
                    <select value={requirements.budget} onChange={(e) => handleTechnicalChange('budget', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Deployment</label>
                    <select value={requirements.deployment} onChange={(e) => handleTechnicalChange('deployment', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="cloud">Cloud</option>
                      <option value="on-premise">On-Premise</option>
                      <option value="edge">Edge</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Compliance</label>
                    <select value={requirements.compliance} onChange={(e) => handleTechnicalChange('compliance', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Priority</label>
                    <select value={requirements.priority} onChange={(e) => handleTechnicalChange('priority', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                      <option value="balanced">Balanced</option>
                      <option value="cost">Cost Optimization</option>
                      <option value="quality">Quality</option>
                      <option value="speed">Speed</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 6: REVIEW & GENERATE */}
          {currentStep === 5 && (
            <motion.div key="step6" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-indigo-50 border-b border-indigo-100 flex items-center gap-3">
                <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold">6</span>
                <h3 className="text-lg font-bold text-gray-900">Review & Generate</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-blue-600">{requirements.industry ? UNIVERSAL_INDUSTRIES[requirements.industry]?.name : 'N/A'}</div>
                    <div className="text-xs text-gray-500">Industry</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-green-600">{requirements.problems.length}</div>
                    <div className="text-xs text-gray-500">Problems</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-purple-600">{requirements.problemTypes.length}</div>
                    <div className="text-xs text-gray-500">Problem Types</div>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-orange-600">{requirements.dataTypes.length}</div>
                    <div className="text-xs text-gray-500">Data Types</div>
                  </div>
                </div>
                <div className="text-center">
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className={`px-10 py-5 rounded-2xl font-bold text-white text-lg transition-all ${
                      isGenerating ? 'bg-gray-400 cursor-wait' : 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:shadow-xl transform hover:scale-105'
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center gap-2"><FiRefreshCw className="w-5 h-5 animate-spin" /> Generating...</span>
                    ) : '🚀 Generate Universal AI Solution'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* NAVIGATION BUTTONS */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))} disabled={currentStep === 0} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${currentStep === 0 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
            <FiArrowLeft className="w-4 h-4" /> Previous
          </button>
          {currentStep < 5 && (
            <button onClick={() => setCurrentStep(prev => Math.min(5, prev + 1))} className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm bg-blue-500 text-white hover:bg-blue-600">
              Next <FiArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* AUTO-CONFIG INFO */}
        <AnimatePresence>
          {showAutoConfigInfo && requirements.problems.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
              <FiInfo className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-700">Auto-Configuration Applied!</p>
                <p className="text-sm text-blue-600">
                  {requirements.problemTypes.length} problem types and {requirements.dataTypes.length} data types auto-configured.
                </p>
              </div>
              <button onClick={() => setShowAutoConfigInfo(false)} className="ml-auto"><FiX className="w-4 h-4 text-blue-500" /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* RESULTS */}
        <div ref={resultsRef}>
          <AnimatePresence>
            {showResults && generatedSolution && (
              <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FiCheckCircle className="w-6 h-6 text-green-500" /> Generated Architecture
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-blue-600">{generatedSolution.selectedComponents?.length || 0}</div>
                    <div className="text-xs text-gray-600">Components</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-green-600">{generatedSolution.summary?.layersCovered || 0}</div>
                    <div className="text-xs text-gray-600">Layers</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-purple-600">${generatedSolution.costModel?.low?.toFixed?.(0) || 0}</div>
                    <div className="text-xs text-gray-600">Low Cost</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-yellow-600">${generatedSolution.costModel?.high?.toFixed?.(0) || 0}</div>
                    <div className="text-xs text-gray-600">High Cost</div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AutoArchitectView;